import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { hypervisorFilterId, timeRangeFilterId } from '../../filters';

const title = t`Hypervisor Network I/O`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['vmNetRxbps', 'vmNetTxbps'],
    },
};

export function useChartConfig() {
    const colorIn = useMetricColor('networkIn');
    const colorOut = useMetricColor('networkOut');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Utilization (Bits/s)`,
                    },
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.BITRATE,
                    chartType: 'spline',
                    key: 'vmNetRxbps',
                    label: t`In`,
                    color: colorIn,
                },
                {
                    formatter: FORMATTER_TYPES.BITRATE,
                    chartType: 'spline',
                    key: 'vmNetTxbps',
                    label: t`Out`,
                    color: colorOut,
                },
            ],
        }),
        [colorIn, colorOut]
    );
}

function HypervisorNetworkThroughputPanel() {
    const chartConfig = useChartConfig();
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

HypervisorNetworkThroughputPanel.propTypes = panelPropTypes;

HypervisorNetworkThroughputPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareHostNetowrkThroughputTrend', // <== original config file was misspelled
    catalogEntry: {
        category: PANEL_CATEGORIES.HYPERVISOR,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Network throughput trend for a selected hypervisor`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, hypervisorFilterId],
        optional: [],
    },
})(HypervisorNetworkThroughputPanel);
